/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFormByFormID } from "./fetchData";
import { Image } from "primereact/image";

interface ViewPermitSpaceEntryFormProps {
  attachedFormData?: any;
}

const ViewPermitSpaceEntryForm = ({
  attachedFormData,
}: ViewPermitSpaceEntryFormProps) => {
  const query = new URLSearchParams(window.location.search);
  const formIDParam = Number(query.get("formID"));
  const ImageNotAvailable = process.env.REACT_APP_IMAGE_NOT_FOUND_URL || "";

  const icon = (
    <i
      className="pi pi-eye tw-text-white tw-h-full tw-w-full tw-justify-center tw-items-center tw-flex"
      style={{
        backgroundColor: "black",
        opacity: 0.5,
        transition: "opacity 0.3s ease",
      }}
    ></i>
  );
  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState<any>(attachedFormData ?? null);
  const [formDataLoading, setFormDataLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  // Convert formJson.preEntryChecks to grouped data format
  const preEntryChecksData =
    formData?.formJson?.preEntryChecks?.map((check: any) => ({
      hazard: check.label,
      controls: check.controlMeasure.map((control: any) => ({
        label: control.label,
        value: control.value,
      })),
    })) || [];

  useEffect(() => {
    if (!attachedFormData) {
      if (!formIDParam) {
        navigate("/layout/health-and-safety/environment-surveying");
      }
      getFormByFormID(
        setFormData,
        setFormDataLoading,
        formIDParam,
        navigate,
        toast
      );
    }
  }, []);

  if (!formData || formDataLoading) {
    return (
      <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-[50px] lg:tw-left-[120px] tw-z-50">
        <ProgressSpinner />
      </div>
    );
  }

  const { formJson, fileJson } = formData;

  // Format date and time from timestamp
  const formatDateTime = (timestamp: number) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Helper to get equipment photo by label
  const getSitePhoto = (label: string) => {
    const photo =
      formData?.fileJson?.siteSetupPhotos &&
      formData?.fileJson?.siteSetupPhotos?.find((img: any) =>
        img.originalname
          .replace(".png", "")
          .toLowerCase()
          .includes(label.toLowerCase())
      );
    return photo?.file || ImageNotAvailable;
  };

  // Helper to get timestamp and location by photoKey
  const getPhotoMeta = (photoKey: string) => {
    const locationObj = formData?.timeStampLocationJson?.locations?.find(
      (loc: any) => loc.photoKey === photoKey
    );
    const timeObj = formData?.timeStampLocationJson?.timeStamps?.find(
      (t: any) => t.photoKey === photoKey
    );
    return {
      address: locationObj?.address || "",
      timestamp: timeObj?.timestamp || "",
    };
  };

  // Get signatures by type and maintain order
  const getSignaturesByType = (type: "approval" | "closure") => {
    const sigs = fileJson.signatures.filter((sig: any) =>
      sig.originalname.includes(`_sig_${type}`)
    );

    // Sort signatures to match the order of names in formJson.name
    return sigs.sort((a: any, b: any) => {
      const aId = a.originalname.split("_")[0];
      const bId = b.originalname.split("_")[0];
      const aIndex = formJson.name.findIndex(
        (n: any) => n.userID.toString() === aId
      );
      const bIndex = formJson.name.findIndex(
        (n: any) => n.userID.toString() === bId
      );
      return aIndex - bIndex;
    });
  };

  // Get user role for display
  const getEntrantTopman = (user: any, type: string) => {
    return (
      formData?.formJson?.entrantTopmans[type].find(
        (u: any) => u?.userID?.toString() === user?.userID?.toString()
      )?.value || "N/A"
    );
    // return "Other Personnel";
  };

  const approvalSignatures = getSignaturesByType("approval");
  const closureSignatures = getSignaturesByType("closure");

  // Format date and time from timestamp
  const formatDate = (timestamp: number) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const HazardRow = ({ hazard, value }: any) => {
    return (
      <tr>
        <td className="tw-align-top tw-font-medium tw-p-2 tw-border tw-bg-gray-200">
          {hazard}
        </td>
        <td className="tw-p-2 tw-border">
          {value === "yes" ? "Yes" : value === "no" ? "No / NA" : "N/A"}
        </td>
      </tr>
    );
  };

  const ControlMeasureRow = ({ control, value }: any) => {
    return (
      <tr>
        <td className="tw-pl-6 tw-font-medium tw-p-2 tw-border">• {control}</td>
        <td className="tw-p-2 tw-border">
          {value === "yes" ? "Yes" : value === "no" ? "No / NA" : "N/A"}
        </td>
      </tr>
    );
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="tw-border-solid tw-border tw-border-[#E2E2e2] tw-rounded-lg tw-relative tw-bg-white">
        <div className="tw-bg-[#F8FAFC] tw-p-4 tw-rounded-t-lg tw-border-b-[1px] tw-border-[#E2E8F0] tw-flex tw-justify-between tw-items-center">
          {!attachedFormData && (
            <h2 className="tw-text-2xl tw-font-semibold tw-text-[#101828]">
              View Permit for Confined Space Entry
            </h2>
          )}
        </div>
        <div>
          <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-px-4">
            <h2 className="tw-text-xl tw-font-semibold tw-text-[#101828]">
              Basic Details
            </h2>
          </div>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 2xl:tw-grid-cols-3 tw-gap-6 tw-px-4 tw-py-4">
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <p className="leak-detail-label">Project</p>
              <p className="leak-value">{formJson.project || "-"}</p>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <p className="leak-detail-label">Names</p>
              <p className="leak-value">
                {formJson.name
                  .map((person: any) => person.displayName)
                  .join(", ")}
              </p>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <p className="leak-detail-label">Site</p>
              <p className="leak-value">{formJson.site || "-"}</p>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <p className="leak-detail-label">Purpose of Entry</p>
              <p className="leak-value">{formJson.purposeOfEntry || "-"}</p>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <p className="leak-detail-label">Description Of Survey Site</p>
              <p className="leak-value">
                {formJson.surveySiteDescription}
                {formJson.surveySiteDescriptionComment &&
                  ` - (${formJson.surveySiteDescriptionComment})`}
              </p>
            </div>
            {formJson.date && (
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Date</p>
                <p className="leak-value">{formatDate(formJson.date)}</p>
              </div>
            )}
          </div>

          <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-px-4">
            <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
              Pre Entry Checks
            </h2>
          </div>
          <div className="tw-w-full tw-my-4 tw-overflow-x-auto lg:tw-min-w-[600px] tw-px-4">
            <table className="tw-w-full tw-border-collapse">
              <thead>
                <tr>
                  <th className="tw-text-left tw-p-2 tw-border tw-bg-gray-100">
                    Hazards associated with task
                  </th>
                  <th className="tw-text-left tw-p-2 tw-border tw-bg-gray-100">
                    Action
                  </th>
                  {/* <th className="tw-text-left tw-p-2 tw-border tw-bg-gray-100">
                    No / NA
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {preEntryChecksData.map((group: any, groupIndex: number) => {
                  const hazardValue = formJson.preEntryChecks[groupIndex].value;
                  return (
                    <React.Fragment key={`hazard-${groupIndex}`}>
                      <HazardRow hazard={group.hazard} value={hazardValue} />
                      {hazardValue === "yes" &&
                        group.controls.map(
                          (control: any, controlIndex: number) => (
                            <ControlMeasureRow
                              key={`control-${groupIndex}-${controlIndex}`}
                              control={control.label}
                              value={control.value}
                            />
                          )
                        )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-px-4 tw-my-4">
            <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
              Safety Essential Equipment Checks Completed
            </h2>
          </div>
          <div className="tw-px-4">
            <DataTable
              className="tw-w-full custome-leak-detection tw-text-black"
              showGridlines
              value={formJson.safetyEssentialEquipmentCheck}
            >
              <Column
                field="label"
                header="Equipment"
                className="tw-text-black tw-font-medium responsive-columns"
              />
              <Column
                header="Action"
                className="lg:tw-w-[100px] tw-w-[80px]"
                body={(rowData) =>
                  rowData.value === "yes"
                    ? "Yes"
                    : rowData.value === "no"
                    ? "No / NA"
                    : rowData.value === "n_a"
                    ? "N/A"
                    : "-"
                }
              />
            </DataTable>
          </div>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-px-4 tw-py-4">
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
              <p className="leak-detail-label">Site Set Up Photo 1</p>
              <div>
                <Image
                  src={getSitePhoto("site_setup-1")}
                  preview
                  alt={`Site Setup Photo 1`}
                  className="tw-min-w-32 tw-min-h-32   tw-max-w-40  tw-shrink-0 tw-object-contain"
                  indicatorIcon={icon}
                  loading="lazy"
                  // downloadable
                  pt={{
                    rotateRightButton: { className: "tw-text-white" },
                    downloadButton: { className: "tw-text-white" },
                    rotateLeftButton: { className: "tw-text-white" },
                    zoomInButton: { className: "tw-text-white" },
                    zoomOutButton: { className: "tw-text-white" },
                    closeButton: { className: "tw-text-white" },
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = ImageNotAvailable;
                  }}
                />
              </div>
                {(getPhotoMeta("site_setup-1").timestamp ||
                  getPhotoMeta("site_setup-1").address) && (
                  <div className="tw-mt-2">
                    <p className="tw-text-sm tw-text-gray-900">
                      {getPhotoMeta("site_setup-1").address}
                    </p>
                    <p className="tw-text-sm tw-text-gray-900">
                      {getPhotoMeta("site_setup-1").timestamp}
                    </p>
                  </div>
                )}
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
              <p className="leak-detail-label">Site Set Up Photo 2</p>
              <div>
                <Image
                  src={getSitePhoto("site_setup-2")}
                  preview
                  alt={`Site Setup Photo 2`}
                  className="tw-min-w-32 tw-min-h-32  tw-max-w-40  tw-shrink-0 tw-object-contain"
                  indicatorIcon={icon}
                  loading="lazy"
                  // downloadable
                  pt={{
                    rotateRightButton: { className: "tw-text-white" },
                    downloadButton: { className: "tw-text-white" },
                    rotateLeftButton: { className: "tw-text-white" },
                    zoomInButton: { className: "tw-text-white" },
                    zoomOutButton: { className: "tw-text-white" },
                    closeButton: { className: "tw-text-white" },
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = ImageNotAvailable;
                  }}
                />
                {(getPhotoMeta("site_setup-2").timestamp ||
                  getPhotoMeta("site_setup-2").address) && (
                  <div className="tw-mt-2">
                    <p className="tw-text-sm tw-text-gray-900">
                      {getPhotoMeta("site_setup-2").address}
                    </p>
                    <p className="tw-text-sm tw-text-gray-900">
                      {getPhotoMeta("site_setup-2").timestamp}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="tw-px-4 tw-py-4 tw-space-y-4">
            {formJson.permitClosureReason && (
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Permit Closure Reason</p>
                <p className="leak-value tw-break-words">
                  {formJson.permitClosureReason}
                </p>
              </div>
            )}

            {formJson.commentsIfAny && (
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">
                  Additional Comments for permit closure:
                </p>
                <p className="leak-value tw-break-words">
                  {formJson.commentsIfAny || "No additional comments"}
                </p>
              </div>
            )}

            {/* Permit Date/Time Section */}
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-pt-2">
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Permit Start Date/Time</p>
                <p className="leak-value">
                  {formJson?.permitStartDateAndTime
                    ? formatDateTime(formJson?.permitStartDateAndTime)
                    : "N/A"}
                </p>
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                <p className="leak-detail-label">Permit End Date/Time</p>
                <p className="leak-value">
                  {formJson?.permitEndDateAndTime
                    ? formatDateTime(formJson?.permitEndDateAndTime)
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-mt-4 tw-px-4 tw-my-4">
            <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
              ACKNOWLEDMENT and Approval of Permit (All Personnel)
            </h2>
          </div>
          <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-4 tw-gap-6 tw-w-full tw-mt-5 tw-px-4">
            {approvalSignatures.map((signature: any, index: number) => {
              const userID = signature.originalname.split("_")[0];
              const user = formJson.name.find(
                (person: any) => person.userID.toString() === userID
              );

              return (
                <div
                  key={index}
                  className="tw-flex tw-flex-col tw-items-start tw-gap-1"
                >
                  <div className="tw-flex tw-items-center tw-gap-2 tw-justify-between tw-w-full">
                    <p className="leak-detail-label tw-text-black tw-max-w-[60%] tw-truncate">
                      {user?.displayName || "Unknown User"}
                    </p>
                    <p className="tw-text-sm">
                      {formatDate(formData.formDate)}
                    </p>
                  </div>
                  <img
                    src={signature.file}
                    alt={`${user?.displayName || "User"} signature`}
                    className="tw-w-full tw-max-h-32 tw-shrink-0 tw-object-contain tw-border"
                  />
                  {/* <p className="leak-detail-label tw-text-black tw-truncate tw-w-full">
                    {user?.displayName || "Unknown User"}
                  </p> */}
                  {
                    <p className="tw-text-normal tw-text-gray-800">
                      Entrant/Topman:{" "}
                      <span className="tw-font-medium tw-text-gray-600">
                        {getEntrantTopman(user, "approvalOfPermit")}
                      </span>
                    </p>
                  }
                </div>
              );
            })}
          </div>

          <div className="tw-pt-4 tw-pb-2 tw-border-b-[1px] tw-border-[#E2E8F0] tw-mt-4 tw-px-4 tw-my-4">
            <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
              ACKNOWLEDMENT OF Permit Closure (All Personnel)
            </h2>
          </div>
          <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-4 tw-gap-6 tw-w-full tw-mt-5 tw-px-4">
            {closureSignatures.map((signature: any, index: number) => {
              const userID = signature.originalname.split("_")[0];
              const user = formJson.name.find(
                (person: any) => person.userID.toString() === userID
              );

              return (
                <div
                  key={index}
                  className="tw-flex tw-flex-col tw-items-start tw-gap-1"
                >
                  <div className="tw-flex tw-items-center tw-gap-2 tw-justify-between tw-w-full">
                    <p className="leak-detail-label tw-text-black tw-max-w-[60%] tw-truncate">
                      {user?.displayName || "Unknown User"}
                    </p>
                    <p className="tw-text-sm">
                      {formatDate(formData.formDate)}
                    </p>
                  </div>
                  <img
                    src={signature.file}
                    alt={`${user?.displayName || "User"} signature`}
                    className="tw-w-full tw-max-h-32 tw-shrink-0 tw-object-contain tw-border"
                  />
                  {/* <p className="leak-detail-label tw-text-black tw-truncate tw-w-full">
                    {user?.displayName || "Unknown User"}
                  </p> */}
                  {
                    <p className="tw-text-normal tw-text-gray-800">
                      Entrant/Topman:{" "}
                      <span className="tw-font-medium tw-text-gray-600">
                        {getEntrantTopman(user, "permitClosure")}
                      </span>
                    </p>
                  }
                </div>
              );
            })}
          </div>

          {!attachedFormData && (
            <div className="tw-flex tw-justify-end tw-items-center tw-p-4 tw-mt-6 tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
              <Button
                className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-border-solid tw-border focus:tw-shadow-none !tw-border-[#344054] tw-text-[#344054] tw-font-medium"
                label="Cancel"
                onClick={() =>
                  navigate(
                    "/layout/health-and-safety/permit-for-confined-space-entry"
                  )
                }
                color="white"
              />
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ViewPermitSpaceEntryForm;
